import Table from '@/components/Table'
import { TableData } from '@/components/Table/type'
import { useResaleQuery } from '@/queries/useResaleQuery'
import { usePDVQuery } from '@/queries/usePDVQuery'
import { PDV } from '@/types/PDV'
import { createColumnHelper } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import Button from "@/components/Button"
import DadosMestreHeaderActions from '../components/DadosMestreHeaderActions'  
import DefaultColumn from '@/components/Table/components/DefaultColumn' 
import { Cog, Search } from 'lucide-react'
import { useTable } from '@/hooks/useTable'   
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from "@/components/ui/select"
import { TypographyH1, TypographyMuted } from '@/components/ui/typography'

export type PDVTable = TableData<PDV>

const columnHelper = createColumnHelper<PDV>()

const MasterDataPDV = () => {
    const { 
        tableState, 
        setTable,  
    } = useTable<PDV>('')
    
    const [selectRevenda, setSelectRevenda] = useState<number>()
    const [enableStatus, setEnableStatus] = useState(false)

    const handleFetchHistoryPayment = () => {  

        if(enableStatus){
            setEnableStatus(true)
        } else {
            refetchPDV()
        }
    }

    const { data: dataRevenda } = useResaleQuery() 
    const { data: dataPDV, isFetching: isFetchingPDV, isLoading: isLoadingPDV, refetch: refetchPDV } = usePDVQuery(selectRevenda || 0, enableStatus)


    const memoData = useMemo(() => dataPDV || [], [dataPDV])
    const memoColumns = useMemo(
        () => [  
            columnHelper.accessor('DS_REVENDA', {
                id: 'DS_REVENDA',
                header: 'Revenda',
                size: 140,
                enableGrouping: false,
            }),  
            columnHelper.accessor('NK_CLIENTE', {
                id: 'NK_CLIENTE',
                header: 'Cod. Cliente',
                size: 120,
                enableGrouping: false,
                aggregatedCell() {
                    return null
                },
            }),  
            columnHelper.accessor('DS_NOME_FANTASIA', {
                id: 'DS_NOME_FANTASIA',
                header: 'Nome Fantasia',
                size: 260,
                enableGrouping: false,
            }),  
            columnHelper.accessor('DS_RAZAO_SOCIAL', {
                id: 'DS_RAZAO_SOCIAL',
                header: 'Razão Social',
                size: 260,
                enableGrouping: false,
            }),  
            columnHelper.accessor('DD_TIPO_PESSOA', {
                id: 'DD_TIPO_PESSOA',
                header: 'Pessoa',
                size: 100,
                cell: ({ row, getValue }) => { 
                    if (row.getIsGrouped()) { 
                        const groupValue = getValue<string>(); 
                        const count = row.subRows.length;  

                        return (
                        <div>
                           {groupValue} Total: ({count})
                        </div>
                        );
                    }

                    return <span>{getValue<string>()}</span>;
                }, 
            }),  
            columnHelper.accessor('DD_UF', {
                id: 'DD_UF',
                header: 'UF',
                size: 80,
                cell: ({ row, getValue }) => { 
                    if (row.getIsGrouped()) { 
                        const groupValue = getValue<string>(); 
                        const count = row.subRows.length;  

                        return (
                        <div>
                           {groupValue} Total: ({count})
                        </div>
                        );
                    }

                    return <span>{getValue<string>()}</span>;
                }, 
            }),  
            columnHelper.accessor('DS_CIDADE', {
                id: 'DS_CIDADE',
                header: 'Cidade',
                size: 140,
                cell: ({ row, getValue }) => { 
                    if (row.getIsGrouped()) { 
                        const groupValue = getValue<string>(); 
                        const count = row.subRows.length;  

                        return (
                        <div>
                           {groupValue} Total: ({count})
                        </div>
                        );
                    }

                    return <span>{getValue<string>()}</span>;
                }, 
            }),  
            columnHelper.accessor('DS_ENDERECO', {
                id: 'DS_ENDERECO',
                header: 'Endereço',
                size: 180,
                enableGrouping: false,
            }),  
            columnHelper.accessor('DS_BAIRRO', {
                id: 'DS_BAIRRO',
                header: 'Bairro',
                size: 180,
                enableGrouping: false,
            }),  
            columnHelper.accessor('DD_COMPLEMENTO', {
                id: 'DD_COMPLEMENTO',
                header: 'Complemento',
                size: 130,
                enableGrouping: false,
            }),  
            columnHelper.accessor('DD_NUMERO', {
                id: 'DD_NUMERO',
                header: 'Número',
                size: 100,
                enableGrouping: false,
                aggregatedCell() {
                    return null
                },
            }),  
            columnHelper.accessor('DD_CNPJ_CPF', {
                id: 'DD_CNPJ_CPF',
                header: 'CNPJ/CPF',
                size: 130,
                enableGrouping: false,
                aggregatedCell() {
                    return null
                },
                
            }),  
            columnHelper.accessor('DD_INSCRICAO_ESTADUAL', {
                id: 'DD_INSCRICAO_ESTADUAL',
                header: 'Inscrição Estadual',
                size: 150,
                enableGrouping: false,
            }),  
            columnHelper.accessor('NM_FUNCIONARIO', {
                id: 'NM_FUNCIONARIO',
                header: 'Func. Responsável',
                size: 150, 
                cell: ({ row, getValue }) => { 
                    if (row.getIsGrouped()) { 
                        const groupValue = getValue<string>(); 
                        const count = row.subRows.length;  

                        return (
                        <div>
                           {groupValue} Total: ({count})
                        </div>
                        );
                    }

                    return <span>{getValue<string>()}</span>;
                }, 
            }),  
            columnHelper.accessor(({ DD_STATUS_ATIVO }) =>
                DD_STATUS_ATIVO == 1
                    ? "Ativo"
                    : "Inativo", {
                id: 'DD_STATUS_ATIVO',
                header: 'Status',
                size: 120,
                filterFn: (row, columnId, filterValue) => {
                    return row.getValue(columnId) === filterValue
                }
            }),  
        ],
        []
    )

    return (
        <div className="flex flex-col h-full overflow-auto p-4 !pb-0">
            <div className="flex flex-col gap-4 justify-between sm:items-center sm:flex-row">
                <div className="flex gap-3 items-center">
                    <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-primary/10">
                        <Cog className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                        <TypographyH1>
                            PDVs
                        </TypographyH1>
                        <TypographyMuted>
                            Consulte todos os PDVs de uma revenda
                        </TypographyMuted>
                    </div>
                </div>
            </div>
            
            <div className="w-full h-full overflow-hidden">
                <Table<PDVTable>
                    data={memoData}
                    columns={memoColumns}
                    getRowId={(row) => row.SK_CLIENTE.toString()}
                    isLoading={isLoadingPDV}
                    isFetching={isFetchingPDV}
                    getTableInstance={(table) => setTable(table)}   
                    tableState={tableState}   
                    expandAllRows={false}
                    tableHeader={
                        <div className='w-full pt-2 flex gap-4'> 
                            <div className="space-y-2 flex flex-col mt-2 w-[200px]">
                                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Revenda</label> 
                                <Select onValueChange={(value) => setSelectRevenda(Number(value))} defaultValue={selectRevenda?.toString()}>
                                    <SelectTrigger> 
                                        <SelectValue />
                                    </SelectTrigger>  
                                    <SelectContent className="w-[200px]">
                                    {dataRevenda?.map((option, index) => (  
                                        <SelectItem key={index} value={option.SK_REVENDA.toString()}>{option.DD_REVENDA}</SelectItem> 
                                    ))}
                                    </SelectContent>
                                </Select>
                            </div>  

                            <div className="space-y-2 flex flex-col mt-2 justify-end"> 
                                <Button  
                                    size="sm" 
                                    onClick={() => handleFetchHistoryPayment()}
                                > 
                                    <Search className='h-[18px]'/>
                                    Pesquisar 
                                </Button>
                            </div>
                        </div>       
                    }
                    tableActions={
                        <DadosMestreHeaderActions 
                        onRefech={refetchPDV} 
                        />
                    }  
                    defaultColumn={{
                        cell: ({ getValue }) => (
                            <DefaultColumn>{getValue() as string}</DefaultColumn>
                        ),
                    }}
                /> 
            </div>
        </div>
    )
}

export default MasterDataPDV
